import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import {
  LogOut, Upload, Trash2, Plus, Save, ExternalLink, Loader2, Lock, ImageIcon, Store,
  Scissors, Images, Users, Star, CalendarDays, ShieldCheck
} from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "@/lib/api";
import { useSite } from "@/lib/site";

const DAYS = ["L", "M", "X", "J", "V", "S", "D"];
const ICON_OPTIONS = ["scissors", "slice", "flame", "droplets", "waves"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-2">{label}</span>
    {children}
  </label>
);

const Card = ({ title, children, actions }) => (
  <div className="border border-white/10 bg-panel rounded-xl p-5">
    <div className="flex items-center justify-between gap-3 mb-4">
      <h3 className="font-display text-sm tracking-[0.14em] text-bone uppercase">{title}</h3>
      {actions}
    </div>
    {children}
  </div>
);

const ImageUploader = ({ label, value, onUploaded }) => {  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      onUploaded(data.path);
      toast.success("Imagen cargada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-2">{label}</span>
      <div className="flex items-center gap-3">
        {value && (
          <img src={mediaUrl(value)} alt="" className="h-14 w-14 object-contain rounded-lg border border-white/10 bg-ink" />
        )}
        <label className="cursor-pointer inline-flex items-center gap-2 text-sm border border-gold/40 text-gold px-4 py-2.5 rounded-full hover:bg-gold hover:text-ink transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {busy ? "Cargando…" : "Subir imagen"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} data-testid="admin-image-upload-input" />
        </label>
      </div>
    </div>
  );
};

const VideoUploader = ({ label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      onChange(data);
      toast.success("Video cargado — presiona Guardar cambios");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  return (
    <div>
      <span className="block font-mono text-[10px] tracking-[0.2em] uppercase text-gold/80 mb-2">{label}</span>
      <div className="flex items-center gap-3 flex-wrap">
        <label className="cursor-pointer inline-flex items-center gap-2 text-sm border border-gold/40 text-gold px-4 py-2.5 rounded-full hover:bg-gold hover:text-ink transition-colors">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
          {busy ? "Cargando…" : value ? "Cambiar video" : "Subir video (MP4)"}
          <input
            data-testid="admin-video-upload-input"
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={upload}
          />
        </label>
        {value && (
          <>
            <span className="font-mono text-[10px] text-gold tracking-widest uppercase" data-testid="admin-video-ready-badge">
              ✓ Video listo
            </span>
            <button
              type="button"
              data-testid="admin-video-remove-button"
              onClick={() => onChange("")}
              className="font-mono text-[10px] uppercase tracking-widest text-mut hover:text-red-400"
            >
              Quitar
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------- Login ----------------
const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState("admin@blackbeard.mx");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onSuccess(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-bone flex items-center justify-center p-5">
      <Toaster richColors position="bottom-center" theme="dark" />
      <form onSubmit={submit} className="w-full max-w-sm border border-gold/15 bg-panel rounded-2xl p-8 flex flex-col gap-5" data-testid="admin-login-form">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 rounded-full border border-gold/30 flex items-center justify-center text-gold mb-4">
            <Lock size={18} />
          </div>
          <h1 className="font-display text-lg tracking-[0.2em] text-bone">BLACKBEARD</h1>
          <p className="text-mut text-sm mt-1">Panel de administración</p>
        </div>
        <Field label="Correo">
          <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="admin-input" required />
        </Field>
        <Field label="Contraseña">
          <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="admin-input" required autoFocus />
        </Field>
        <button
          data-testid="admin-login-button"
          type="submit"
          disabled={busy}
          className="bg-gold text-ink font-semibold rounded-full py-3 hover:bg-goldlight transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          Entrar
        </button>
        <a href="/" className="text-center text-xs text-mut hover:text-gold transition-colors">← Volver al sitio</a>
      </form>
    </div>
  );
};

// ---------------- Tabs ----------------
const GeneralTab = () => {
  const { data, refresh } = useSite();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data?.settings && !form) setForm({ ...data.settings });
  }, [data]);

  if (!form) return <LoadingBlock />;

  const s = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const hourRow = (i, k) => (e) => {
    const hours = [...form.hours];
    hours[i] = { ...hours[i], [k]: e.target.value };
    setForm({ ...form, hours });
  };
  const toggleDay = (i, d) => {
    const hours = [...form.hours];
    const days = hours[i].days.includes(d) ? hours[i].days.filter((x) => x !== d) : [...hours[i].days, d];
    hours[i] = { ...hours[i], days };
    setForm({ ...form, hours });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", { ...form, hours: form.hours || [] });
      await refresh();
      toast.success("Cambios guardados");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <Card title="Identidad">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Nombre del negocio"><input data-testid="admin-business-name-input" className="admin-input" value={form.business_name || ""} onChange={s("business_name")} /></Field>
          <Field label="Teléfono visible"><input data-testid="admin-phone-input" className="admin-input" value={form.phone || ""} onChange={s("phone")} /></Field>
          <Field label="WhatsApp (solo números, con código de país)"><input data-testid="admin-whatsapp-input" className="admin-input" value={form.whatsapp || ""} onChange={s("whatsapp")} /></Field>
          <Field label="Link de Google Maps"><input data-testid="admin-maps-input" className="admin-input" value={form.maps_url || ""} onChange={s("maps_url")} /></Field>
          <Field label="Dirección"><input data-testid="admin-address-input" className="admin-input" value={form.address || ""} onChange={s("address")} /></Field>
          <Field label="Instagram (opcional)"><input className="admin-input" value={form.instagram || ""} onChange={s("instagram")} /></Field>
          <Field label="Frase (ES)"><input className="admin-input" value={form.tagline_es || ""} onChange={s("tagline_es")} /></Field>
          <Field label="Frase (EN)"><input className="admin-input" value={form.tagline_en || ""} onChange={s("tagline_en")} /></Field>
          <Field label="Sobre nosotros (ES)"><textarea rows={3} className="admin-input resize-none" value={form.about_es || ""} onChange={s("about_es")} /></Field>
          <Field label="Sobre nosotros (EN)"><textarea rows={3} className="admin-input resize-none" value={form.about_en || ""} onChange={s("about_en")} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-5 mt-5">
          <ImageUploader label="Logo (se muestra en el hero)" value={form.hero_logo_path} onUploaded={(p) => setForm({ ...form, hero_logo_path: p })} />
          <ImageUploader label="Imagen de fondo del hero" value={form.hero_bg_path} onUploaded={(p) => setForm({ ...form, hero_bg_path: p })} />
        </div>
        <div className="mt-5">
          <VideoUploader
            label="Video promocional (se muestra entre la galería y el equipo)"
            value={form.promo_video_path || ""}
            onChange={(d) =>
              setForm(
                d && d.path
                  ? { ...form, promo_video_path: d.path, promo_poster_path: d.poster_path || "" }
                  : { ...form, promo_video_path: "", promo_poster_path: "" },
              )
            }
          />
        </div>
      </Card>

      <Card title="Horario">
        <div className="flex flex-col gap-4">
          {(form.hours || []).map((h, i) => (
            <div key={i} className="grid sm:grid-cols-[1fr_1fr_90px_90px] gap-3 items-end" data-testid={`admin-hour-row-${i}`}>
              <Field label="Días (ES)"><input className="admin-input" value={h.days_es} onChange={hourRow(i, "days_es")} /></Field>
              <Field label="Days (EN)"><input className="admin-input" value={h.days_en} onChange={hourRow(i, "days_en")} /></Field>
              <Field label="Abre"><input type="time" className="admin-input [color-scheme:dark]" value={h.open} onChange={hourRow(i, "open")} /></Field>
              <Field label="Cierra"><input type="time" className="admin-input [color-scheme:dark]" value={h.close} onChange={hourRow(i, "close")} /></Field>
              <div className="sm:col-span-4 flex items-center gap-1.5">
                <span className="font-mono text-[10px] text-mut mr-2 uppercase tracking-widest">Días activos:</span>
                {DAYS.map((d, di) => (
                  <button
                    key={di}
                    type="button"
                    onClick={() => toggleDay(i, di)}
                    className={`h-8 w-8 rounded-full text-xs font-semibold transition-colors ${
                      (h.days || []).includes(di) ? "bg-gold text-ink" : "border border-white/15 text-mut hover:border-gold/50"
                    }`}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <button
        data-testid="admin-save-changes-button"
        onClick={save}
        disabled={busy}
        className="self-start bg-gold text-ink font-semibold px-8 py-3 rounded-full hover:bg-goldlight transition-colors disabled:opacity-50 flex items-center gap-2"
      >
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
        Guardar cambios
      </button>
    </div>
  );
};

const LoadingBlock = () => (
  <div className="flex items-center gap-2 text-mut text-sm py-10 justify-center">
    <Loader2 size={16} className="animate-spin" /> Cargando…
  </div>
);

const ServicesTab = () => {
  const { data, refresh } = useSite();
  const services = data?.services || [];

  const add = async () => {
    await api.post("/services", { name_es: "Nuevo servicio", name_en: "New service", icon: "scissors", sort: services.length + 1 });
    await refresh();
  };
  const save = async (svc) => {
    await api.put(`/services/${svc.id}`, svc);
    await refresh();
    toast.success("Servicio guardado");
  };
  const remove = async (id) => {
    await api.delete(`/services/${id}`);
    await refresh();
  };

  return (
    <div className="flex flex-col gap-4">
      <button data-testid="admin-add-service-button" onClick={add} className="self-start inline-flex items-center gap-2 text-sm border border-gold/40 text-gold px-5 py-2.5 rounded-full hover:bg-gold hover:text-ink transition-colors">
        <Plus size={15} /> Agregar servicio
      </button>
      {services.map((svc) => (
        <ServiceCard key={svc.id} svc={svc} onSave={save} onDelete={remove} />
      ))}
    </div>
  );
};

const ServiceCard = ({ svc: initial, onSave, onDelete }) => {
  const [svc, setSvc] = useState(initial);
  useEffect(() => setSvc(initial), [initial]);
  const s = (k) => (e) => setSvc({ ...svc, [k]: e.target.value });

  return (
    <div className="border border-white/10 bg-panel rounded-xl p-5 grid md:grid-cols-[1fr_1fr_1.4fr_130px_100px] gap-4 items-end" data-testid={`admin-service-card-${initial.id}`}>
      <Field label="Nombre (ES)"><input className="admin-input" value={svc.name_es} onChange={s("name_es")} /></Field>
      <Field label="Name (EN)"><input className="admin-input" value={svc.name_en} onChange={s("name_en")} /></Field>
      <Field label="Descripción (ES / EN)">
        <div className="flex flex-col gap-2">
          <input className="admin-input" value={svc.desc_es} onChange={s("desc_es")} placeholder="ES" />
          <input className="admin-input" value={svc.desc_en} onChange={s("desc_en")} placeholder="EN" />
        </div>
      </Field>
      <Field label="Ícono">
        <select className="admin-input" value={svc.icon} onChange={s("icon")}>
          {ICON_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </Field>
      <div className="flex gap-2">
        <button data-testid={`admin-save-service-${initial.id}`} onClick={() => onSave(svc)} className="h-10 w-10 rounded-full bg-gold text-ink flex items-center justify-center hover:bg-goldlight transition-colors" title="Guardar"><Save size={15} /></button>
        <button data-testid={`admin-delete-service-${initial.id}`} onClick={() => onDelete(svc.id)} className="h-10 w-10 rounded-full border border-red-500/40 text-red-400 flex items-center justify-center hover:bg-red-500/10 transition-colors" title="Eliminar"><Trash2 size={15} /></button>
      </div>
    </div>
  );
};

const GalleryTab = () => {
  const { data, refresh } = useSite();
  const [cat, setCat] = useState("cortes");
  const [busy, setBusy] = useState(false);
  const items = data?.gallery || [];

  const upload = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data: up } = await api.post("/upload", fd);
        await api.post("/gallery", { image_path: up.path, category: cat, sort: items.length + 1 });
      }
      await refresh();
      toast.success("Imágenes agregadas a la galería");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/gallery/${id}`);
    await refresh();
  };

  const update = async (id, patch) => {
    const item = items.find((g) => g.id === id);
    await api.put(`/gallery/${id}`, { ...item, ...patch });
    await refresh();
  };

  const uploadBefore = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd);
      await update(id, { before_path: up.path });
      toast.success("Foto ANTES agregada — abre la galería para ver el comparador");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <Card title="Subir imágenes">
        <div className="flex flex-wrap items-center gap-4">
          <Field label="Categoría">
            <select data-testid="admin-gallery-category-select" className="admin-input w-40" value={cat} onChange={(e) => setCat(e.target.value)}>
              <option value="cortes">Cortes</option>
              <option value="barbas">Barbas</option>
              <option value="estilo">Estilo</option>
            </select>
          </Field>
          <label className="cursor-pointer inline-flex items-center gap-2 text-sm bg-gold text-ink font-semibold px-5 py-2.5 rounded-full hover:bg-goldlight transition-colors mt-5">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
            {busy ? "Cargando…" : "Subir a la galería"}
            <input data-testid="admin-gallery-upload-input" type="file" accept="image/*" multiple className="hidden" onChange={upload} />
          </label>
        </div>
      </Card>

      <div data-testid="admin-gallery-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((g) => (
          <div key={g.id} className="relative group border border-white/10 rounded-lg overflow-hidden" data-testid={`admin-gallery-item-${g.id}`}>
            <img src={mediaUrl(g.image_path)} alt="" className="w-full h-36 object-cover" />
            <span className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-widest bg-ink/80 text-gold px-2 py-1 rounded">{g.category}</span>
            {g.before_path && (
              <span className="absolute top-2 left-16 font-mono text-[9px] uppercase tracking-widest bg-gold text-ink px-2 py-1 rounded" data-testid={`admin-before-badge-${g.id}`}>
                Antes/Después
              </span>
            )}
            <button
              data-testid={`admin-delete-gallery-${g.id}`}
              onClick={() => remove(g.id)}
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-ink/80 text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              title="Quitar"
            >
              <Trash2 size={14} />
            </button>
            <div className="absolute bottom-0 inset-x-0 flex items-center justify-between gap-1 bg-ink/85 px-2 py-1.5">
              {g.before_path ? (
                <>
                  <span className="font-mono text-[8px] uppercase tracking-widest text-gold">Foto antes lista</span>
                  <button
                    data-testid={`admin-remove-before-${g.id}`}
                    onClick={() => update(g.id, { before_path: "" })}
                    className="font-mono text-[8px] uppercase tracking-widest text-mut hover:text-red-400"
                  >
                    Quitar
                  </button>
                </>
              ) : (
                <label className="cursor-pointer font-mono text-[8px] uppercase tracking-widest text-mut hover:text-gold">
                  + Subir foto ANTES
                  <input
                    data-testid={`admin-before-upload-${g.id}`}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => uploadBefore(g.id, e)}
                  />
                </label>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const TeamTab = () => {
  const { data, refresh } = useSite();
  const team = data?.team || [];

  const add = async () => {
    await api.post("/team", { name: "Nuevo barbero", role_es: "Barbero", role_en: "Barber", sort: team.length + 1 });
    await refresh();
  };
  const save = async (m) => {
    await api.put(`/team/${m.id}`, m);
    await refresh();
    toast.success("Miembro guardado");
  };
  const remove = async (id) => {
    await api.delete(`/team/${id}`);
    await refresh();
  };

  return (
    <div className="flex flex-col gap-4">
      <button data-testid="admin-add-team-button" onClick={add} className="self-start inline-flex items-center gap-2 text-sm border border-gold/40 text-gold px-5 py-2.5 rounded-full hover:bg-gold hover:text-ink transition-colors">
        <Plus size={15} /> Agregar barbero
      </button>
      {team.map((m) => (
        <TeamCard key={m.id} member={m} onSave={save} onDelete={remove} />
      ))}
    </div>
  );
};

const TeamCard = ({ member: initial, onSave, onDelete }) => {
  const [m, setM] = useState(initial);
  useEffect(() => setM(initial), [initial]);
  const s = (k) => (e) => setM({ ...m, [k]: e.target.value });

  return (
    <div className="border border-white/10 bg-panel rounded-xl p-5 grid md:grid-cols-[140px_1fr_1fr_auto] gap-4 items-center" data-testid={`admin-team-card-${initial.id}`}>
      <ImageUploader label="Foto" value={m.photo_path} onUploaded={(p) => setM({ ...m, photo_path: p })} />
      <Field label="Nombre"><input className="admin-input" value={m.name} onChange={s("name")} /></Field>
      <Field label="Rol (ES / EN)">
        <div className="flex flex-col gap-2">
          <input className="admin-input" value={m.role_es} onChange={s("role_es")} placeholder="ES" />
          <input className="admin-input" value={m.role_en} onChange={s("role_en")} placeholder="EN" />
        </div>
      </Field>
      <div className="flex gap-2">
        <button data-testid={`admin-save-team-${initial.id}`} onClick={() => onSave(m)} className="h-10 w-10 rounded-full bg-gold text-ink flex items-center justify-center hover:bg-goldlight transition-colors" title="Guardar"><Save size={15} /></button>
        <button data-testid={`admin-delete-team-${initial.id}`} onClick={() => onDelete(m.id)} className="h-10 w-10 rounded-full border border-red-500/40 text-red-400 flex items-center justify-center hover:bg-red-500/10 transition-colors" title="Eliminar"><Trash2 size={15} /></button>
      </div>
    </div>
  );
};

const ReviewsTab = () => {
  const { data, refresh } = useSite();
  const items = data?.testimonials || [];

  const add = async () => {
    await api.post("/testimonials", { name: "Cliente", text_es: "", text_en: "", rating: 5, sort: items.length + 1 });
    await refresh();
  };
  const save = async (r) => {
    await api.put(`/testimonials/${r.id}`, r);
    await refresh();
    toast.success("Reseña guardada");
  };
  const remove = async (id) => {
    await api.delete(`/testimonials/${id}`);
    await refresh();
  };

  return (
    <div className="flex flex-col gap-4">
      <button data-testid="admin-add-review-button" onClick={add} className="self-start inline-flex items-center gap-2 text-sm border border-gold/40 text-gold px-5 py-2.5 rounded-full hover:bg-gold hover:text-ink transition-colors">
        <Plus size={15} /> Agregar reseña
      </button>
      {items.map((r) => (
        <ReviewCard key={r.id} review={r} onSave={save} onDelete={remove} />
      ))}
    </div>
  );
};

const ReviewCard = ({ review: initial, onSave, onDelete }) => {
  const [r, setR] = useState(initial);
  useEffect(() => setR(initial), [initial]);
  const s = (k) => (e) => setR({ ...r, [k]: e.target.value });

  return (
    <div className="border border-white/10 bg-panel rounded-xl p-5 grid md:grid-cols-[160px_1fr_90px_auto] gap-4 items-end" data-testid={`admin-review-card-${initial.id}`}>
      <Field label="Cliente"><input className="admin-input" value={r.name} onChange={s("name")} /></Field>
      <Field label="Texto (ES / EN)">
        <div className="flex flex-col gap-2">
          <textarea rows={2} className="admin-input resize-none" value={r.text_es} onChange={s("text_es")} placeholder="ES" />
          <textarea rows={2} className="admin-input resize-none" value={r.text_en} onChange={s("text_en")} placeholder="EN" />
        </div>
      </Field>
      <Field label="Estrellas">
        <select className="admin-input" value={r.rating} onChange={s("rating")}>
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </Field>
      <div className="flex gap-2">
        <button data-testid={`admin-save-review-${initial.id}`} onClick={() => onSave(r)} className="h-10 w-10 rounded-full bg-gold text-ink flex items-center justify-center hover:bg-goldlight transition-colors" title="Guardar"><Save size={15} /></button>
        <button data-testid={`admin-delete-review-${initial.id}`} onClick={() => onDelete(r.id)} className="h-10 w-10 rounded-full border border-red-500/40 text-red-400 flex items-center justify-center hover:bg-red-500/10 transition-colors" title="Eliminar"><Trash2 size={15} /></button>
      </div>
    </div>
  );
};

const BookingsTab = () => {
  const { data, refresh } = useSite();
  const [subs, setSubs] = useState(null);

  const load = async () => {
    try {
      const { data: list } = await api.get("/submissions");
      setSubs(list);
    } catch {
      setSubs([]);
    }
  };
  useEffect(() => { load(); }, [data]);

  const remove = async (id) => {
    await api.delete(`/submissions/${id}`);
    await load();
  };

  if (subs === null) return <LoadingBlock />;
  if (!subs.length) return <p className="text-mut text-sm py-10 text-center" data-testid="admin-bookings-empty">Aún no hay solicitudes de cita.</p>;

  return (
    <div className="flex flex-col gap-3" data-testid="admin-bookings-list">
      {subs.map((b) => (
        <div key={b.id} className="border border-white/10 bg-panel rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`admin-booking-${b.id}`}>
          <div className="min-w-0">
            <p className="text-bone font-semibold" data-testid={`admin-booking-name-${b.id}`}>{b.name} <span className="text-mut font-normal font-mono text-xs ml-2">{b.phone}</span></p>
            <p className="text-mut text-sm mt-1">
              {[b.service, b.date, b.time].filter(Boolean).join(" · ")}
              {b.message ? ` — “${b.message}”` : ""}
            </p>
          </div>
          <button
            data-testid={`admin-delete-booking-${b.id}`}
            onClick={() => remove(b.id)}
            className="ml-auto h-9 w-9 rounded-full border border-red-500/40 text-red-400 flex items-center justify-center hover:bg-red-500/10 transition-colors"
            title="Eliminar"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
    </div>
  );
};

const SecurityTab = () => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Contraseña actualizada");
      setCur(""); setNext("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-sm flex flex-col gap-4" data-testid="admin-security-form">
      <Card title="Cambiar contraseña">
        <div className="flex flex-col gap-4">
          <Field label="Contraseña actual">
            <input data-testid="admin-current-password-input" type="password" className="admin-input" value={cur} onChange={(e) => setCur(e.target.value)} required />
          </Field>
          <Field label="Nueva contraseña (mín. 8 caracteres)">
            <input data-testid="admin-new-password-input" type="password" className="admin-input" value={next} onChange={(e) => setNext(e.target.value)} required minLength={8} />
          </Field>
          <button data-testid="admin-change-password-button" type="submit" disabled={busy} className="bg-gold text-ink font-semibold rounded-full py-3 hover:bg-goldlight transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
            Actualizar contraseña
          </button>
        </div>
      </Card>
    </form>
  );
};

// ---------------- Dashboard ----------------
const TABS = [
  ["general", "General", Store],
  ["services", "Servicios", Scissors],
  ["gallery", "Galería", Images],
  ["team", "Equipo", Users],
  ["reviews", "Opiniones", Star],
  ["bookings", "Citas", CalendarDays],
  ["security", "Seguridad", ShieldCheck],
];

const Dashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("general");
  const { data } = useSite();
  const s = data?.settings || {};

  return (
    <div className="min-h-screen bg-ink text-bone" data-testid="admin-dashboard">
      <Toaster richColors position="bottom-center" theme="dark" />
      <header className="border-b border-gold/10 bg-panel sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {s.hero_logo_path && <img src={mediaUrl(s.hero_logo_path)} alt="" className="h-9 w-auto" />}
            <span className="font-display text-sm tracking-[0.18em] hidden sm:block">ADMIN — {s.business_name || "BLACKBEARD"}</span>
          </div>
          <div className="flex items-center gap-2">
            <a data-testid="admin-view-site-link" href="/" target="_blank" className="inline-flex items-center gap-2 text-sm text-mut hover:text-gold transition-colors border border-white/10 px-4 py-2 rounded-full">
              <ExternalLink size={14} /> Ver sitio
            </a>
            <button data-testid="admin-logout-button" onClick={onLogout} className="inline-flex items-center gap-2 text-sm border border-white/10 text-mut hover:text-red-400 px-4 py-2 rounded-full transition-colors">
              <LogOut size={14} /> Salir
            </button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 pb-3 flex gap-2 overflow-x-auto">
          {TABS.map(([id, label, Icon]) => (
            <button
              key={id}
              data-testid={`admin-tab-${id}`}
              onClick={() => setTab(id)}
              className={`inline-flex items-center gap-2 text-sm px-4 py-2 rounded-full border whitespace-nowrap transition-colors ${
                tab === id ? "bg-gold text-ink border-gold font-semibold" : "border-white/10 text-mut hover:border-gold/40 hover:text-gold"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-8">
        {tab === "general" && <GeneralTab />}
        {tab === "services" && <ServicesTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "team" && <TeamTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "security" && <SecurityTab />}
      </main>

      <footer className="border-t border-white/5 py-6 text-center font-mono text-[10px] tracking-[0.2em] text-mut/40 uppercase">
        <ImageIcon size={10} className="inline mr-1" />
        Las imágenes subidas y los cambios se publican de inmediato en el sitio
      </footer>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-ink text-gold flex items-center justify-center font-display tracking-[0.3em]">
        BLACKBEARD
      </div>
    );
  }
  if (user === null) return <Login onSuccess={setUser} />;
  return <Dashboard user={user} onLogout={() => api.post("/auth/logout").finally(() => setUser(null))} />;
}
